import React, { useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button-v1'
import { Sheet, SheetContent, SheetOverlay } from '@wartek-id/sheet-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'

import { useBCKSInvitationReview } from './useBCKSInvitationReview'
import { KSReviewSheetContent } from '../blocks/KSReviewSheetContent'
import Header from 'app/BuktiKaryaPage/components/Header'
import useKSPSTrackEvent from '../hooks/useKSPSTrackEvent'
import SectionInfoGaruda from '../components/SectionInfoGaruda'
import { useBCKSDesktopView } from 'app/BCKSPage/hooks/useBCKSDesktopView'
import { BCKSDesktopPageShell } from 'app/BCKSPage/components/BCKSDesktopPageShell'

const reviewDescription =
  'Cek kembali berkas yang diunggah dan pastikan sudah benar. Setelah dikumpulkan, berkas tidak dapat diganti/dihapus.'

export const BCKSInvitationReview = () => {
  const { KSPSTrackEvent } = useKSPSTrackEvent()
  const {
    documentData,
    handleChangeDocument,
    handleFinalizedDocument,
    handleNextStep,
    sheetType,
    sheetOpen,
    setSheetOpen,
    setSheetType,
    handleSaveDraft,
  } = useBCKSInvitationReview()

  useEffect(() => {
    KSPSTrackEvent({
      event_name: 'enter_file_review_page',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { isDesktop } = useBCKSDesktopView()
  const documentEntries = Object.entries(documentData)

  const handleSubmitClick = () => {
    setSheetOpen(true)
    KSPSTrackEvent({
      event_name: 'click_file_review_submit',
    })
  }

  const handleSaveDraftClick = () => {
    handleSaveDraft()
    KSPSTrackEvent({
      event_name: 'click_file_review_save_draft',
    })
  }

  if (isDesktop) {
    return (
      <BCKSDesktopPageShell title="Cek Berkas" pageLabel="Cek Berkas">
        <div className="pb-8 lg:pb-10">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
            <div className="space-y-4">
              {documentEntries.map(([documentName, currentDocument]) => (
                <div
                  className="rounded-lg border border-outline-default bg-white p-5"
                  key={documentName}
                >
                  <div className="flex items-start justify-between gap-3">
                    <Text variant="action" className="flex-1">
                      {currentDocument.title}
                    </Text>
                  </div>

                  <Link
                    href={`/bcks/invitation/review/${documentName}`}
                    passHref
                  >
                    <a className="mt-4 block overflow-hidden rounded border border-outline-default bg-surface-subdued transition hover:border-interactive-default">
                      <div className="relative h-[240px] w-full">
                        <Image
                          src={currentDocument.thumbnailUrl}
                          alt={currentDocument.title}
                          layout="fill"
                          objectFit="cover"
                        />
                      </div>
                    </a>
                  </Link>

                  <Button
                    color="white"
                    fullWidth
                    className="mt-4"
                    onClick={() =>
                      handleChangeDocument({
                        ...currentDocument,
                        name: documentName,
                      })
                    }
                  >
                    Ganti Dokumen
                  </Button>
                </div>
              ))}
            </div>

            <div className="lg:sticky lg:top-24">
              <div className="rounded-lg border border-outline-default bg-white p-5 lg:p-6">
                <Text variant="heading-sm">Siap dikumpulkan?</Text>
                <Text color="subdued" className="mt-2">
                  {reviewDescription}
                  {/* {`${documentEntries.length} dokumen sudah siap diperiksa. Simpan sebagai draf jika ingin kembali nanti.`} */}
                </Text>

                <SectionInfoGaruda className="mt-5 rounded-xl" />

                <div className="mt-5 space-y-3">
                  <Button
                    fullWidth
                    size="lg"
                    color="blue"
                    onClick={handleSubmitClick}
                  >
                    Kumpulkan
                  </Button>

                  <Button
                    fullWidth
                    size="lg"
                    color="white"
                    onClick={handleSaveDraftClick}
                  >
                    Simpan Draf
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Dialog isOpen={sheetOpen} onClose={() => setSheetOpen(false)}>
          <DialogOverlay />
          <DialogContent className="max-w-xl m-auto p-4 self-center">
            <KSReviewSheetContent
              sheetType={sheetType}
              onChangeSheetType={setSheetType}
              onChangeSheetState={setSheetOpen}
              onSubmissionClick={() => handleFinalizedDocument()}
              onContinueClick={() => handleNextStep()}
            />
          </DialogContent>
        </Dialog>
      </BCKSDesktopPageShell>
    )
  }

  return (
    <div className="relative flex flex-col h-screen max-w-xl mx-auto">
      <Header title="Cek Berkas" forceNavigateBack />
      <div className="p-4">
        <Text color="subdued" className="mb-2">
          {reviewDescription}
        </Text>
        <div>
          {documentEntries.map(([documentName, currentDocument]) => (
            <div className="my-8" key={documentName}>
              <Text variant="action">{currentDocument.title}</Text>
              <Link href={`/bcks/invitation/review/${documentName}`} passHref>
                <a className="my-2 block max-h-[185px] overflow-hidden rounded border border-[#808489]">
                  <Image
                    src={currentDocument.thumbnailUrl}
                    alt={currentDocument.title}
                    width={500}
                    height={500}
                  />
                </a>
              </Link>
              <Button
                color="white"
                fullWidth
                onClick={() =>
                  handleChangeDocument({
                    ...currentDocument,
                    name: documentName,
                  })
                }
              >
                Ganti Dokumen
              </Button>
            </div>
          ))}
        </div>

        <Sheet isOpen={sheetOpen} onClose={setSheetOpen} type="alert">
          <SheetOverlay />
          <SheetContent className="max-w-xl m-auto p-4">
            <KSReviewSheetContent
              sheetType={sheetType}
              onChangeSheetType={setSheetType}
              onChangeSheetState={setSheetOpen}
              onSubmissionClick={() => handleFinalizedDocument()}
              onContinueClick={() => handleNextStep()}
            />
          </SheetContent>
        </Sheet>

        <SectionInfoGaruda className="mb-6" />

        <div className="flex flex-row items-center mt-4">
          <Button
            fullWidth
            className="mr-2"
            size="lg"
            color="white"
            onClick={handleSaveDraftClick}
          >
            Simpan Draf
          </Button>

          <Button
            fullWidth
            className="ml-2"
            size="lg"
            color="blue"
            onClick={handleSubmitClick}
          >
            Kumpulkan
          </Button>
        </div>
      </div>
    </div>
  )
}
